import React from 'react';

import { QUEUE_PER_PAGE } from './useHomeQueue';

import Alert from 'CommonComponents/Alert';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

/**
 * The shell every unified list shares: one table, one pagination control, one
 * empty state.
 *
 * ONE PAGINATION CONTROL FOR THE WHOLE LIST — the five per-type tabs it
 * replaces each carried their own pager, and a user looking for one
 * requisition had to walk five of them.
 *
 * It knows nothing about record types: `columns` and `renderRow` come from the
 * view, so a column set can change in one place without this file having an
 * opinion about it.
 *
 * `emptyState` is rendered INSTEAD of the table when the list is empty, never
 * as a row inside it. A table of column headers over "No records found" is
 * still an empty table.
 */
export default function QueueTable({
    columns,
    rows,
    renderRow,
    loading,
    error,
    total,
    from,
    to,
    lastPage,
    page,
    perPage = QUEUE_PER_PAGE,
    onPageChange,
    emptyState,
    pagerSummary,
}) {
    const isEmpty = !loading && !error && rows.length === 0;

    /* ONE pager, rendered at both ends. A user who has read to the bottom of ten
       rows should not scroll back up to reach page 2 — every per-type list this
       replaces put one below the table, and dropping to a single control at the
       top would be a regression dressed as consolidation. Both instances drive
       the same `onPageChange`, so there is no second piece of state to disagree. */
    const paginated = total > perPage;

    /* The caption is NOT part of the pager, and is rendered whether or not the
       list is long enough to page. It names the filters the figures were counted
       under, and the reader who most needs it is the one holding eight rows in a
       bucket they have to take on trust is the bucket they asked for. */
    const pager = (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
            <PaginationInfo from={from} to={to} total={total} />
            {!!pagerSummary && <div className="tw-text-xs tw-text-charcoal-60">{pagerSummary}</div>}
            {paginated && <TablePagination lastPage={lastPage} pageChange={(selection) => onPageChange(selection.selected + 1)} currentPage={page} />}
        </div>
    );

    const showPager = paginated || !!pagerSummary;

    return (
        <div className="table__card">
            {loading && <LoadingSpinner />}

            {!!error && !loading && <Alert type="error">{$t('We could not load this list. Please refresh to try again.')}</Alert>}

            {isEmpty && emptyState}

            {!loading && !error && rows.length > 0 && (
                <>
                    {showPager && <div className="tw-mb-4">{pager}</div>}

                    <div className="inbud-table_wrapper">
                        <table className="inbud-table">
                            <thead>
                                <tr>
                                    {columns.map((column) => (
                                        <th key={column.key} className={column.className}>
                                            {column.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>{rows.map(renderRow)}</tbody>
                        </table>
                    </div>

                    {showPager && <div className="tw-mt-4">{pager}</div>}
                </>
            )}
        </div>
    );
}
